import { PlusOutlined } from '@ant-design/icons'
import { UserRole } from '@lmc/ts-common'
import { Button, PageHeader } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { serviceRecordConstants } from 'locale/serviceRecords'
import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import useRoleAccess from '../../../../components/AccessControl/useRoleAccess'
import { ServiceRecordList } from './components/ServiceRecordList'
import ServiceRecordModal from './components/ServiceRecordModal'
import { useServiceRecordsList } from './hooks/useServiceRecordsList'
import type { ServiceRecord } from './types'

export const ResidentServiceRecordsScreen: React.FC = () => {
  const { t } = useTranslation(NAMESPACE.SERVICE_RECORDS)
  const { id: residentId } = useParams<{ id: string }>()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedServiceRecord, setSelectedServiceRecord] = useState<
    ServiceRecord | undefined
  >(undefined)

  const {
    serviceRecords,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useServiceRecordsList({
    residentId: residentId || '',
    limit: 50
  })

  const hasAddAccess = useRoleAccess(
    [UserRole.lmcAdmin, UserRole.careHomeAdmin, UserRole.seniorCarer],
    [
      {
        i: 'create',
        this: 'ServiceRecord'
      }
    ]
  )

  const handleAddClick = () => {
    setSelectedServiceRecord(undefined)
    setIsModalVisible(true)
  }

  const handleEdit = (serviceRecord: ServiceRecord) => {
    setSelectedServiceRecord(serviceRecord)
    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    setSelectedServiceRecord(undefined)
  }

  return (
    <>
      <PageHeader title={t(serviceRecordConstants.SERVICE_RECORDS)} sticky>
        {hasAddAccess && (
          <Button variant="primary" onClick={handleAddClick}>
            <PlusOutlined />
            {t(serviceRecordConstants.ADD_SERVICE_RECORD)}
          </Button>
        )}
      </PageHeader>

      <ServiceRecordList
        serviceRecords={serviceRecords}
        isLoading={isLoading}
        onEdit={handleEdit}
        onRefetch={refetch}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />

      <ServiceRecordModal
        isVisible={isModalVisible}
        onClose={handleCloseModal}
        onSuccess={handleCloseModal}
        serviceRecord={selectedServiceRecord}
        residentId={residentId || ''}
      />
    </>
  )
}
